import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { User, Phone, ShieldCheck, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Button3D } from '@/components/learner/ui/Button3D'
import { getApiErrorMessage } from '@/utils/apiError'
import { useResendCooldown } from '@/hooks/useResendCooldown'
import {
  getRecoveryRateLimitCooldownSeconds,
  getRecoveryRateLimitMessage,
  isRecoveryDailyRateLimit,
} from '@/utils/recoveryRateLimit'
import {
  requestAdminRecoveryCode,
  verifyAdminFindId,
  verifyAdminPasswordReset,
  confirmAdminPasswordReset,
  type AdminRecoveryPurpose,
  type AdminRecoveryAccount,
} from '@/api/adminAccountRecovery'

interface Props {
  isOpen: boolean
  onClose: () => void
}

type Step = 'input' | 'code' | 'result'

const FIELD_LABEL = 'block text-[14px] font-semibold text-lc-gray-800 mb-1.5'
const FIELD_SHELL =
  'flex items-center gap-3 h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white transition focus-within:border-[1.5px] focus-within:border-lc-lightblue-900'
const FIELD_INPUT =
  'flex-1 bg-transparent outline-none text-lc-body-2 text-lc-gray-700 placeholder-lc-gray-500'

export function AdminAccountRecoverySelfServiceModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation('learner')
  const [purpose, setPurpose] = useState<AdminRecoveryPurpose>('find-id')
  const [step, setStep] = useState<Step>('input')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [emails, setEmails] = useState<string[]>([])
  const [resetToken, setResetToken] = useState('')
  const [accounts, setAccounts] = useState<AdminRecoveryAccount[]>([])
  const [selectedUser, setSelectedUser] = useState<number | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [resetDone, setResetDone] = useState(false)
  const [dailyLimitReached, setDailyLimitReached] = useState(false)
  const { secondsLeft, startCooldown, clearCooldown, isBlocked: resendBlocked } = useResendCooldown()

  if (!isOpen) return null

  const switchTab = (p: AdminRecoveryPurpose) => {
    setPurpose(p)
    setStep('input')
    setError(null)
    setCode('')
    setEmails([])
    setAccounts([])
    setSelectedUser(null)
    setNewPassword('')
    setResetDone(false)
    setDailyLimitReached(false)
    clearCooldown()
  }

  const wrap = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      setBusy(false)
    }
  }

  const onSend = () =>
    wrap(async () => {
      try {
        await requestAdminRecoveryCode(purpose, name, phone)
        setStep('code')
        setDailyLimitReached(false)
        startCooldown()
      } catch (e) {
        const rateLimitMessage = getRecoveryRateLimitMessage(e, t)
        if (rateLimitMessage) {
          if (isRecoveryDailyRateLimit(e)) {
            setDailyLimitReached(true)
          } else {
            const cooldown = getRecoveryRateLimitCooldownSeconds(e)
            if (cooldown) startCooldown(cooldown)
          }
          setError(rateLimitMessage)
          return
        }
        throw e
      }
    })

  const onVerify = () =>
    wrap(async () => {
      if (purpose === 'find-id') {
        const r = await verifyAdminFindId(name, phone, code)
        setEmails(r.emails)
      } else {
        const r = await verifyAdminPasswordReset(name, phone, code)
        setResetToken(r.resetToken)
        setAccounts(r.accounts)
        setSelectedUser(r.accounts.length === 1 ? r.accounts[0].userId : null)
      }
      setStep('result')
    })

  const onConfirmReset = () =>
    wrap(async () => {
      if (selectedUser == null) return
      await confirmAdminPasswordReset(resetToken, selectedUser, newPassword)
      setResetDone(true)
    })

  const tabClass = (active: boolean) =>
    `flex-1 h-9 rounded-[10px] text-[15px] font-semibold transition ${
      active ? 'bg-white text-lc-gray-900 shadow-sm' : 'text-lc-gray-500'
    }`

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-[400px] rounded-lc-xl shadow-lc-soft ring-0 p-0 gap-0 overflow-hidden">
        <div className="p-7">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-lc-headline-3 text-lc-gray-900 pr-8">
              {t('auth.adminRecovery.modalTitle')}
            </DialogTitle>
          </DialogHeader>

          {/* 세그먼트 탭: 아이디 찾기 / 비밀번호 재설정 */}
          <div className="flex gap-1 mb-6 p-1 bg-lc-gray-100 rounded-lc-md">
            <button
              type="button"
              onClick={() => switchTab('find-id')}
              aria-pressed={purpose === 'find-id'}
              className={tabClass(purpose === 'find-id')}
            >
              {t('auth.adminRecovery.tabFindId')}
            </button>
            <button
              type="button"
              onClick={() => switchTab('password-reset')}
              aria-pressed={purpose === 'password-reset'}
              className={tabClass(purpose === 'password-reset')}
            >
              {t('auth.adminRecovery.tabResetPw')}
            </button>
          </div>

          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <span className={FIELD_LABEL}>{t('auth.adminRecovery.name')}</span>
                <div className={FIELD_SHELL}>
                  <User className="size-5 text-lc-gray-500 shrink-0" aria-hidden />
                  <input
                    aria-label={t('auth.adminRecovery.name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={FIELD_INPUT}
                  />
                </div>
              </div>
              <div>
                <span className={FIELD_LABEL}>{t('auth.adminRecovery.phone')}</span>
                <div className={FIELD_SHELL}>
                  <Phone className="size-5 text-lc-gray-500 shrink-0" aria-hidden />
                  <input
                    aria-label={t('auth.adminRecovery.phone')}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="numeric"
                    placeholder="01012345678"
                    className={FIELD_INPUT}
                  />
                </div>
              </div>
              <Button3D
                type="button"
                variant="lightblue"
                size="L"
                disabled={busy || !name || !phone}
                onClick={onSend}
                className="w-full"
              >
                {t('auth.adminRecovery.sendCode')}
              </Button3D>
            </div>
          )}

          {step === 'code' && (
            <div className="space-y-4">
              <div>
                <span className={FIELD_LABEL}>{t('auth.adminRecovery.code')}</span>
                <div className={FIELD_SHELL}>
                  <ShieldCheck className="size-5 text-lc-gray-500 shrink-0" aria-hidden />
                  <input
                    aria-label={t('auth.adminRecovery.code')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className={`${FIELD_INPUT} tracking-[0.3em]`}
                  />
                </div>
              </div>
              <Button3D
                type="button"
                variant="lightblue"
                size="L"
                disabled={busy || !code}
                onClick={onVerify}
                className="w-full"
              >
                {t('auth.adminRecovery.verify')}
              </Button3D>
              <button
                type="button"
                disabled={busy || resendBlocked || dailyLimitReached}
                onClick={onSend}
                className="w-full text-center text-lc-body-3 text-lc-lightblue-dark hover:underline disabled:opacity-50"
              >
                {resendBlocked
                  ? t('auth.adminRecovery.resendIn', { seconds: secondsLeft })
                  : t('auth.adminRecovery.resend')}
              </button>
            </div>
          )}

          {step === 'result' && purpose === 'find-id' && (
            <div className="space-y-4">
              <h3 className="text-lc-body-2 font-semibold text-lc-gray-900">
                {t('auth.adminRecovery.foundEmailsTitle')}
              </h3>
              <ul className="space-y-2">
                {emails.map((e) => (
                  <li
                    key={e}
                    className="px-4 py-3 rounded-lc-md bg-lc-gray-100 font-semibold text-lc-gray-900 break-all select-all"
                  >
                    {e}
                  </li>
                ))}
              </ul>
              <Button3D type="button" variant="lightblue" size="L" onClick={onClose} className="w-full">
                {t('auth.adminRecovery.done')}
              </Button3D>
            </div>
          )}

          {step === 'result' && purpose === 'password-reset' && !resetDone && (
            <div className="space-y-4">
              {accounts.length > 1 && (
                <div>
                  <span className={FIELD_LABEL}>{t('auth.adminRecovery.selectAccount')}</span>
                  <select
                    aria-label={t('auth.adminRecovery.selectAccount')}
                    value={selectedUser ?? ''}
                    onChange={(e) => setSelectedUser(Number(e.target.value))}
                    className="w-full h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white text-lc-body-2 text-lc-gray-700 outline-none focus:border-[1.5px] focus:border-lc-lightblue-900"
                  >
                    <option value="" disabled>
                      {t('auth.adminRecovery.selectAccount')}
                    </option>
                    {accounts.map((a) => (
                      <option key={a.userId} value={a.userId}>
                        {a.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <span className={FIELD_LABEL}>{t('auth.adminRecovery.newPassword')}</span>
                <div className={FIELD_SHELL}>
                  <Lock className="size-5 text-lc-gray-500 shrink-0" aria-hidden />
                  <input
                    type={showPw ? 'text' : 'password'}
                    aria-label={t('auth.adminRecovery.newPassword')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={FIELD_INPUT}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="size-8 inline-flex items-center justify-center text-lc-gray-500 hover:text-lc-gray-700 rounded-md"
                    aria-label={showPw ? t('auth.password.hide') : t('auth.password.show')}
                    aria-pressed={showPw}
                  >
                    {showPw ? <EyeOff className="size-[18px]" aria-hidden /> : <Eye className="size-[18px]" aria-hidden />}
                  </button>
                </div>
              </div>
              <Button3D
                type="button"
                variant="lightblue"
                size="L"
                disabled={busy || selectedUser == null || !newPassword}
                onClick={onConfirmReset}
                className="w-full"
              >
                {t('auth.adminRecovery.confirmReset')}
              </Button3D>
            </div>
          )}

          {step === 'result' && purpose === 'password-reset' && resetDone && (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="size-12 text-lc-mint-900 mx-auto" aria-hidden />
              <p className="text-lc-body-2 font-semibold text-lc-gray-900">
                {t('auth.adminRecovery.done')}
              </p>
              <Button3D type="button" variant="lightblue" size="L" onClick={onClose} className="w-full">
                {t('auth.adminRecovery.done')}
              </Button3D>
            </div>
          )}

          {error && (
            <p role="alert" className="mt-4 text-lc-body-3 text-lc-red-dark">
              {error}
            </p>
          )}

          <p className="mt-6 text-center text-lc-body-3 text-lc-gray-600">
            {t('auth.adminRecovery.fallbackContact')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
